import { Box, Button, createStyles, Theme, Typography } from "@material-ui/core";
import { OTPublisher, OTSession, OTStreams, OTSubscriber } from "opentok-react";
import React from "react";
import { useIntl } from "react-intl";
import { getLocalizedValue } from "../../core/lang";
import { ViewAs } from "../../corporation";
import { MeetingProps } from "./index";
import { MeetingHeader } from "./MeetingHeader";
import { OpenTokArgs, useOpenTok } from "./opentok";
import { makeStyles } from "@material-ui/core/styles";
const OT = require("@opentok/client");

type Props = {
  sessionId: string;
  token: string;
  apiKey: string;
  onError: (err: any) => void;
  onClose: () => void;
  publish?: boolean;
} & OpenTokArgs &
  MeetingProps;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    waitingBox: {
      backgroundColor: theme.palette.common.white,
      position: 'relative',
      width: '100%',
      height: '0',
      paddingBottom: '56.25%'
    },
    waitingText: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)'
    }
  }),
);

export const MeetingCallPanel = ({
  sessionId,
  token,
  apiKey,
  onError,
  publish,
  expert,
  corporation,
  locale,
  date,
  productName,
  productDescription,
  participant,
  viewAs,
  onClose,
  ...openTokArgs
}: Props) => {
  const { formatMessage, formatDate } = useIntl();
  const classes = useStyles();

  const {
    sessionEventHandlers,
    publisherEventHandlers,
    subscriberEventHandlers,
    toggleVideo,
    publishVideo,
    waitingForParticipant,
  } = useOpenTok({
    ...openTokArgs,
    createdAt: date.startDate,
  });

  return (
    <div>
      <MeetingHeader
        expert={viewAs === ViewAs.Expert ? participant : expert}
        corporation={corporation}
        locale={locale}
        date={date}
        title={formatMessage(
          {
            id: "MeetingCallTitle",
            defaultMessage: "Willkommen zu Ihrem Termin mit {name}.",
          },
          { name: getLocalizedValue(locale, corporation.corpName) }
        )}
        text={formatMessage(
          {
            id: "MeetingCallText",
            defaultMessage: `Im Rahmen der WindEnergy Hamburg vom 1.12. bis 4.12.2020 haben Sie 
                            für {dayName}, den {startDate} von {startTime} bis {endTime} Uhr 
                            einen Gesprächstermin mit {corpName} verinbart.`,
          },
          {
            dayName: formatDate(date.startDate, {
              weekday: "long",
            }),
            startDate: formatDate(date.startDate),
            startTime: formatDate(date.startDate, {
              hour: "numeric",
              minute: "numeric",
            }),
            endTime: formatDate(date.endDate, {
              hour: "numeric",
              minute: "numeric",
            }),
            corpName: getLocalizedValue(locale, corporation.corpName),
            expertName: expert.fullName,
          }
        )}
      />
      <OTSession
        apiKey={apiKey}
        sessionId={sessionId}
        token={token}
        onError={onError}
        eventHandlers={sessionEventHandlers}
      >
        {publish && (
          <>
            <Box display="flex" justifyContent="space-between">
              <Box>
                <Button id="videoButton" onClick={toggleVideo}>
                  {formatMessage(
                    publishVideo
                    ? {
                        id: "MeetingCallDisableVideo",
                        defaultMessage: "Video deaktivieren",
                      }
                    : {
                        id: "MeetingCallEnableVideo",
                        defaultMessage: "Video aktivieren",
                      }
                  )}
                </Button>
              </Box>
              <OTPublisher
                properties={{
                  showControls: true,
                  publishVideo,
                  width: 100,
                  height: 100,
                }}
                onError={onError}
                eventHandlers={publisherEventHandlers}
              />
            </Box>
          </>
        )}
        <OTStreams>
          <OTSubscriber
            onError={onError}
            eventHandlers={subscriberEventHandlers}
            properties={{
              height: 0,
              width: "100%",
            }}
          />
        </OTStreams>
      </OTSession>
      {waitingForParticipant && (
        <Box mt={2}
             className={classes.waitingBox}>
          <Typography className={classes.waitingText} variant="subtitle1" color="primary">
            {formatMessage({
              id: "MeetingCallWaiting",
              defaultMessage: "Waiting for participant...",
            })}
          </Typography>
        </Box>
      )}

      <Box mt={2}>
        <Button onClick={onClose}>
          {formatMessage({
            id: "MeetingCallClose",
            defaultMessage: "Meeting Beenden",
          })}
        </Button>
      </Box>
    </div>
  );
};
